import { useCallback, useEffect, useMemo, useState } from 'react'

/**
 * Clave única en sessionStorage para persistir los filtros de la pantalla
 * "Pendientes de validación" entre navegaciones intra-SPA.
 */
const SESSION_KEY = 'filtros_pendientes_validacion'

const FILTROS_INICIALES = Object.freeze({
  fechaDesde: '',
  fechaHasta: '',
  tipo: '',
  centro: '',
  texto: '',
})

/**
 * Lee los filtros persistidos de sessionStorage. Si la clave no existe o el
 * payload está corrupto, devuelve los filtros iniciales sin lanzar.
 */
function leerSession() {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return { ...FILTROS_INICIALES }
  }
  try {
    const raw = window.sessionStorage.getItem(SESSION_KEY)
    if (!raw) {
      return { ...FILTROS_INICIALES }
    }
    const parsed = JSON.parse(raw)
    return { ...FILTROS_INICIALES, ...parsed }
  } catch {
    return { ...FILTROS_INICIALES }
  }
}

/**
 * Trunca una fecha (string ISO o Date) al inicio de su día local. Devuelve
 * null cuando el valor está vacío o no es parseable.
 */
function truncarADiaLocal(valor) {
  if (!valor) return null
  const fecha = valor instanceof Date ? valor : new Date(valor)
  if (Number.isNaN(fecha.getTime())) return null
  return new Date(fecha.getFullYear(), fecha.getMonth(), fecha.getDate()).getTime()
}

/**
 * Construye el value compuesto que identifica a un tipo en el filtro: el
 * dropdown agrega por la etiqueta `d_tipo` pero internamente conservamos
 * `(tipo, subtipo)` para no colisionar entre subtipos con el mismo `d_tipo`.
 */
function valueTipo(tipo, subtipo) {
  return `${tipo ?? ''}|${subtipo ?? ''}`
}

/**
 * Hook que encapsula la lógica de filtrado client-side de la pantalla
 * "Solicitudes pendientes de validación":
 *   - Calcula las opciones únicas de tipo y centro a partir del array de
 *     solicitudes recibido (sin aplicar filtros).
 *   - Aplica un predicado AND entre rango de fechas, tipo y centro.
 *   - Persiste el estado de los filtros en `sessionStorage` para que
 *     sobrevivan a navegaciones intra-SPA.
 *
 * @param {Array<Object>} solicitudes Listado completo que el usuario puede
 *   ver según permisos. Cada item incluye al menos `fecha_creacion`, `tipo`,
 *   `subtipo`, `d_tipo`, `centro` y `d_centro`.
 * @returns {{
 *   filtros: { fechaDesde: string, fechaHasta: string, tipo: string, centro: string },
 *   tiposDisponibles: Array<{ value: string, label: string }>,
 *   centrosDisponibles: Array<{ value: string, label: string }>,
 *   solicitudesFiltradas: Array<Object>,
 *   setFiltro: (clave: string, valor: string) => void,
 *   limpiarFiltros: () => void,
 * }}
 */
export default function useFiltrosPendientes(solicitudes) {
  const [filtros, setFiltros] = useState(() => leerSession())

  const lista = useMemo(() => (Array.isArray(solicitudes) ? solicitudes : []), [solicitudes])

  const tiposDisponibles = useMemo(() => {
    const mapa = new Map()
    for (const s of lista) {
      const value = valueTipo(s?.tipo, s?.subtipo)
      const label = (s?.d_tipo ?? '').toString()
      if (!label) continue
      if (!mapa.has(value)) {
        mapa.set(value, { value, label })
      }
    }
    return Array.from(mapa.values()).sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
    )
  }, [lista])

  const centrosDisponibles = useMemo(() => {
    const mapa = new Map()
    for (const s of lista) {
      const codigo = (s?.centro ?? '').toString()
      if (!codigo) continue
      const descripcion = (s?.d_centro ?? '').toString()
      const label = descripcion ? `${codigo} — ${descripcion}` : codigo
      if (!mapa.has(codigo)) {
        mapa.set(codigo, { value: codigo, label })
      }
    }
    return Array.from(mapa.values()).sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
    )
  }, [lista])

  const solicitudesFiltradas = useMemo(() => {
    const desde = truncarADiaLocal(filtros.fechaDesde)
    const hasta = truncarADiaLocal(filtros.fechaHasta)
    const tipoSel = filtros.tipo
    const centroSel = filtros.centro
    const textoSel = (filtros.texto ?? '').trim().toLowerCase()

    return lista.filter((s) => {
      // Rango de fechas inclusivo; extremo vacío = sin cota.
      if (desde !== null || hasta !== null) {
        const dia = truncarADiaLocal(s?.fecha_creacion)
        if (dia === null) return false
        if (desde !== null && dia < desde) return false
        if (hasta !== null && dia > hasta) return false
      }
      if (tipoSel && valueTipo(s?.tipo, s?.subtipo) !== tipoSel) {
        return false
      }
      if (centroSel && (s?.centro ?? '') !== centroSel) {
        return false
      }
      if (textoSel) {
        // Réplica del filtro interno previo de Table: aplana un nivel y busca
        // case-insensitive en cualquier valor escalar de la solicitud.
        const valores = Object.values(s ?? {}).flat()
        const coincide = valores.some(
          (v) => v != null && v.toString().toLowerCase().includes(textoSel),
        )
        if (!coincide) return false
      }
      return true
    })
  }, [lista, filtros])

  // Persiste cada cambio de filtros en sessionStorage. Si todos los filtros
  // están vacíos (estado equivalente al inicial), eliminamos la clave para
  // dejar el storage limpio.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.sessionStorage) return
    const todosVacios =
      !filtros.fechaDesde &&
      !filtros.fechaHasta &&
      !filtros.tipo &&
      !filtros.centro &&
      !filtros.texto
    try {
      if (todosVacios) {
        window.sessionStorage.removeItem(SESSION_KEY)
      } else {
        window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(filtros))
      }
    } catch {
      // sessionStorage puede estar deshabilitado (modo privado en algunos
      // navegadores). El filtrado en memoria sigue funcionando.
    }
  }, [filtros])

  const setFiltro = useCallback((clave, valor) => {
    setFiltros((prev) => ({ ...prev, [clave]: valor ?? '' }))
  }, [])

  const limpiarFiltros = useCallback(() => {
    setFiltros({ ...FILTROS_INICIALES })
  }, [])

  return {
    filtros,
    tiposDisponibles,
    centrosDisponibles,
    solicitudesFiltradas,
    setFiltro,
    limpiarFiltros,
  }
}
